/**
 * @file index.tsx
 *
 * @fileoverview Renders the Hotjar script in the page. Consent check is based on GA4 component.
 */

import React, { useContext, useEffect, useRef } from 'react';
import { checkUserConsentForService } from '~/jobiqo/services/cmp';
import { UserContext, UserContextInterface } from '~/jobiqo/context/user';
import { SERVICES } from '~/jobiqo/lib/cmp';
import Router from 'next/router';
import Script from 'next/script';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';

const usePageReloadIfHotjarConsentGotRemoved = (currentHotjarConsent) => {
  // If the consent has changed from true to false we need to reload
  // the page otherwise Hotjar does not get excluded.
  const previousHotjarConsent = useRef<boolean>(null);
  const userContext: UserContextInterface = useContext(UserContext);

  // Save current consent state to compare.
  useEffect(() => {
    previousHotjarConsent.current = checkUserConsentForService(
      userContext?.consents,
      SERVICES.Hotjar,
    );
  }, [userContext.consents]);

  // Compare if consent has changed from true to false.
  if (previousHotjarConsent.current && !currentHotjarConsent) {
    Router.reload();
  }
};

export const Hotjar = () => {
  const userContext: UserContextInterface = useContext(UserContext);
  const { APP } = useFrontendConfig();
  const testing = process.env.testing;
  const staging = process.env.staging;

  const isUserConsentNeededAndGiven = checkUserConsentForService(
    userContext?.consents,
    SERVICES.Hotjar,
  );

  usePageReloadIfHotjarConsentGotRemoved(isUserConsentNeededAndGiven);

  return APP.HOTJAR_ID &&
    isUserConsentNeededAndGiven &&
    !testing &&
    !staging ? (
    <>
      <Script id="Hotjar-script" type="text/javascript" async={true}>{`
      (function (h, o, t, j, a, r) {
        h.hj = h.hj || function () {(h.hj.q = h.hj.q || []).push(arguments);};
        h._hjSettings = { hjid: ${APP.HOTJAR_ID}, hjsv: 6 };
        a = o.getElementsByTagName('head')[0];
        r = o.createElement('script');
        r.async = 1;
        r.src = t + h._hjSettings.hjid + j + h._hjSettings.hjsv;
        a.appendChild(r);
      })(window, document, 'https://static.hotjar.com/c/hotjar-', '.js?sv=');
      `}</Script>
    </>
  ) : null;
};

export default Hotjar;
